import type { ReactNode } from 'react';
import { useHasFeatureAccess, useRequiredPlanForFeature } from '../hooks';
import type { SubscriptionFeature } from '../types';
import { LockedFeatureCard } from './LockedFeatureCard';

interface SubscriptionGateProps {
  /**
   * The feature to check access for
   */
  feature: SubscriptionFeature;
  /**
   * Content to render when the user has access
   */
  children: ReactNode;
  /**
   * Optional custom fallback when user doesn't have access
   * Defaults to LockedFeatureCard
   */
  fallback?: ReactNode;
  /**
   * If true, render nothing instead of the fallback when access is denied
   */
  hideWhenLocked?: boolean;
}

/**
 * Gate component that conditionally renders content based on subscription access
 *
 * @example
 * // Basic usage
 * <SubscriptionGate feature="ads">
 *   <AdsSection />
 * </SubscriptionGate>
 *
 * @example
 * // With custom fallback
 * <SubscriptionGate
 *   feature="employees"
 *   fallback={<UpgradeBanner feature="employees" />}
 * >
 *   <EmployeeList />
 * </SubscriptionGate>
 *
 * @example
 * // Hide when locked
 * <SubscriptionGate feature="priceComparison" hideWhenLocked>
 *   <PriceComparisonButton />
 * </SubscriptionGate>
 */
export function SubscriptionGate({
  feature,
  children,
  fallback,
  hideWhenLocked = false,
}: SubscriptionGateProps) {
  const hasAccess = useHasFeatureAccess(feature);
  const requiredPlan = useRequiredPlanForFeature(feature);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (hideWhenLocked) {
    return null;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return <LockedFeatureCard feature={feature} requiredPlan={requiredPlan} />;
}
